import { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import type { PendingInvite } from "../api/teams.js";
import { paths } from "../app/paths.js";
import { usePendingInvites } from "../data/teams.js";
import { useToast } from "../ui/Toast.js";

/**
 * Toasts once per invite and session when a new team invite shows up. The sidebar badge and the header bell
 * keep it reachable after the toast is gone. Skipped on the pages that already show the invite.
 */
export function useInviteNotifications(): PendingInvite[] {
  const pending = usePendingInvites();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const seen = useRef<Set<string>>(new Set());
  // Stale invites never notify; they only show up on the Teams page so they can be removed.
  const invites = useMemo(
    (): PendingInvite[] | undefined => pending.data?.filter((invite: PendingInvite): boolean => !invite.stale),
    [pending.data]
  );

  useEffect((): void => {
    if (invites === undefined) {
      return;
    }
    const fresh = invites.filter((invite: PendingInvite): boolean => !seen.current.has(invite.id));
    for (const invite of invites) {
      seen.current.add(invite.id);
    }
    const onInvitePage = location.pathname === paths.teams || location.pathname === paths.invite;
    const first = fresh[0];
    if (first === undefined || onInvitePage) {
      return;
    }
    const message = fresh.length === 1
      ? `Du wurdest ins Team ${first.orgName} eingeladen.`
      : `Du hast ${fresh.length} neue Team-Einladungen.`;
    toast.notify(message, {
      action: { label: "Ansehen", run: (): void => void navigate(paths.teams) }
    });
  }, [invites, location.pathname, navigate, toast]);

  return invites ?? [];
}
